// File upload utilities for Grupa AI - React implementation

import config from '../config';

export const getUserFolderMapping = async (userEmail) => {
  try {
    // Use relative URL to work with both development and production
    const baseUrl = config.apiUrl;
    const response = await fetch(`${baseUrl}/get-user-folder?email=${encodeURIComponent(userEmail)}`);
    if (!response.ok) {
      throw new Error('Failed to get user folder mapping');
    }
    const result = await response.json(); // { company, user, email, cached }
    console.log('📋 getUserFolderMapping response:', result);
    return result;
  } catch (error) {
    console.warn('Failed to get user folder mapping, using fallback:', error);
    // Fallback to email slug conversion
    const userSlug = userEmail.replace(/[@.]/g, '-');
    return {
      company: userSlug,
      user: userSlug,
      email: userEmail,
      cached: false,
      fallback: true
    };
  }
};

export const uploadFile = async (file, userEmail, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    // Get user's actual folder mapping (handles dynamic company names)
    onProgress?.(10, 'Finding user folder...');
    const mapping = await getUserFolderMapping(userEmail);

    // Handle null/undefined mapping with fallback
    let orgId, userId;
    if (!mapping || !mapping.company || !mapping.user) {
      console.warn('Invalid mapping received, using fallback:', mapping);
      const userSlug = userEmail.replace(/[@.]/g, '-');
      const fallbackMapping = {
        company: userSlug,
        user: userSlug,
        email: userEmail,
        cached: false,
        fallback: true
      };
      orgId = fallbackMapping.company;
      userId = fallbackMapping.user;
    } else {
      orgId = mapping.company;
      userId = mapping.user;
    }

    onProgress?.(20, 'Preparing upload...');

    // Use relative URL to work with both development and production
    const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';

    // Create XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          const uploadProgress = 20 + (percentComplete * 0.6); // 20-80% for upload
          onProgress?.(uploadProgress, `Uploading ${percentComplete}%...`);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          onProgress?.(90, 'Processing document...');
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.status === 'success') {
              onProgress?.(100, 'Upload complete!');
              resolve(result);
            } else {
              reject(new Error(result.message || 'Upload processing failed'));
            }
          } catch (parseError) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      onProgress?.(30, 'Starting upload...');
      xhr.open('POST', `${baseUrl}/upload/${orgId}/${userId}?email=${encodeURIComponent(userEmail)}`);
      xhr.send(formData);
    });

  } catch (error) {
    console.error('Upload error:', error);
    onProgress?.(0, 'Upload failed');
    throw error;
  }
};

export const getUserFiles = async (userEmail) => {
  try {
    // Get user's actual folder mapping (handles dynamic company names)
    const mapping = await getUserFolderMapping(userEmail);

    // Handle null/undefined mapping with fallback
    let orgId, userId;
    if (!mapping || !mapping.company || !mapping.user) {
      console.warn('Invalid mapping received in getUserFiles, using fallback:', mapping);
      const userSlug = userEmail.replace(/[@.]/g, '-');
      const fallbackMapping = {
        company: userSlug,
        user: userSlug,
        email: userEmail,
        cached: false,
        fallback: true
      };
      orgId = fallbackMapping.company;
      userId = fallbackMapping.user;
    } else {
      orgId = mapping.company;
      userId = mapping.user;
    }

    console.log(`🔍 Fetching files for org: ${orgId}, user: ${userId}`);

    // Use relative URL to work with both development and production
    const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';
    const response = await fetch(`${baseUrl}/files/${orgId}/${userId}`);

    if (!response.ok) {
      console.warn(`❌ API call failed with status ${response.status}, using empty list`);
      return []; // Return empty list instead of throwing error
    }

    const data = await response.json();
    console.log(`✅ Files API response:`, data);
    console.log(`📊 Found ${data.files?.length || 0} files for user`);

    return data.files || [];
  } catch (error) {
    console.warn('❌ Failed to fetch files, returning empty list:', error);
    return []; // Return empty list instead of throwing error
  }
};

export const deleteFile = async (fileId) => {
  try {
    const response = await fetch(`/api/files/${fileId}`, {
      method: 'DELETE',
    });

    return response.ok;
  } catch (error) {
    console.error('Failed to delete file:', error);
    return false;
  }
};

// Helper function to get file icon based on file type
export const getFileIcon = (filename) => {
  const extension = filename.split('.').pop()?.toLowerCase();

  const iconMap = {
    // Documents
    pdf: { icon: 'PDF', color: '#E34C4C' },
    doc: { icon: 'DOC', color: '#2E7CE6' },
    docx: { icon: 'DOC', color: '#2E7CE6' },
    txt: { icon: 'TXT', color: '#666666' },
    rtf: { icon: 'RTF', color: '#666666' },

    // Spreadsheets
    xls: { icon: 'XLS', color: '#2E7D32' },
    xlsx: { icon: 'XLS', color: '#2E7D32' },
    csv: { icon: 'CSV', color: '#2E7D32' },

    // Presentations
    ppt: { icon: 'PPT', color: '#E37A40' },
    pptx: { icon: 'PPT', color: '#E37A40' },

    // Images
    jpg: { icon: 'IMG', color: '#9C27B0' },
    jpeg: { icon: 'IMG', color: '#9C27B0' },
    png: { icon: 'IMG', color: '#9C27B0' },
    gif: { icon: 'IMG', color: '#9C27B0' },
    svg: { icon: 'SVG', color: '#9C27B0' },
    webp: { icon: 'IMG', color: '#9C27B0' },

    // Code
    js: { icon: 'JS', color: '#F7DF1E' },
    ts: { icon: 'TS', color: '#3178C6' },
    html: { icon: 'HTML', color: '#E34C26' },
    css: { icon: 'CSS', color: '#1572B6' },
    py: { icon: 'PY', color: '#3776AB' },
    json: { icon: 'JSON', color: '#000000' },
    xml: { icon: 'XML', color: '#0060AC' },

    // Design
    fig: { icon: 'FIG', color: '#A259FF' },
    sketch: { icon: 'SKT', color: '#F7B500' },
    psd: { icon: 'PSD', color: '#31A8FF' },
    ai: { icon: 'AI', color: '#FF7C00' },

    // Archives
    zip: { icon: 'ZIP', color: '#7B3F00' },
    rar: { icon: 'RAR', color: '#7B3F00' },
    '7z': { icon: '7Z', color: '#7B3F00' },
    tar: { icon: 'TAR', color: '#7B3F00' },
    gz: { icon: 'GZ', color: '#7B3F00' },

    // Video
    mp4: { icon: 'VID', color: '#FF0000' },
    avi: { icon: 'VID', color: '#FF0000' },
    mov: { icon: 'VID', color: '#FF0000' },
    wmv: { icon: 'VID', color: '#FF0000' },
    flv: { icon: 'VID', color: '#FF0000' },

    // Audio
    mp3: { icon: 'AUD', color: '#FF6B35' },
    wav: { icon: 'AUD', color: '#FF6B35' },
    flac: { icon: 'AUD', color: '#FF6B35' },
    aac: { icon: 'AUD', color: '#FF6B35' },

    // Default
    default: { icon: 'FILE', color: '#666666' }
  };

  return iconMap[extension] || iconMap.default;
};

// Format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Convert supabase:// URL to signed URL for file access
export const getSignedUrl = async (supabaseUrl) => {
  if (!supabaseUrl || !supabaseUrl.startsWith('supabase://')) {
    return supabaseUrl; // Return as-is if not a supabase URL
  }

  try {
    // Extract path from supabase://bucket/path
    const urlMatch = supabaseUrl.match(/supabase:\/\/([^/]+)\/(.+)/);
    if (!urlMatch) {
      console.error('Invalid supabase URL format:', supabaseUrl);
      return null;
    }

    const [, bucket, path] = urlMatch;

    // Get signed URL from backend
    const response = await fetch(`${config.apiUrl}/storage/signed-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bucket,
        path
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to get signed URL: ${response.status}`);
    }

    const data = await response.json();
    return data.signedUrl;
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return null;
  }
};

// Handle file click with proper type detection and opening
export const handleFileClick = async (file) => {
  console.log('handleFileClick called with:', file);

  if (!file.url || file.isUploading) {
    console.log('File has no URL or is uploading');
    return;
  }

  let finalUrl = file.url;

  // Resolve URL if it's a supabase URL
  if (file.url.startsWith('supabase://')) {
    const signedUrl = await getSignedUrl(file.url);
    console.log('Got signed URL:', signedUrl);
    if (!signedUrl) {
      console.error('Failed to generate signed URL for file:', file.url);
      return;
    }
    finalUrl = signedUrl;
  }

  // Check file type and handle accordingly
  const fileName = file.name.toLowerCase();
  console.log('File name for matching:', fileName);
  console.log('Final URL:', finalUrl);

  if (fileName.endsWith('.pdf')) {
    console.log('Handling as PDF');
    // Use blob rendering for PDFs
    const pdfBlobUrl = await renderPdf(finalUrl);
    if (pdfBlobUrl) {
      // Create a new window with PDF embed
      const newWindow = window.open('', '_blank');
      newWindow.document.write(`
        <html>
          <head><title>${file.name}</title></head>
          <body style="margin:0;padding:0;">
            <embed src="${pdfBlobUrl}" type="application/pdf" width="100%" height="100%" />
          </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      console.error('Failed to render PDF blob');
      // Fallback to direct open
      window.open(finalUrl, '_blank');
    }
  } else if (fileName.match(/\.(doc|docx|xls|xlsx|ppt|pptx|csv)$/)) {
    // Office documents - preview with Microsoft Office Online Viewer
    // This is the standard "1-line" way to view Office files in the browser
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(finalUrl)}`;

    // Try Microsoft Viewer first
    const previewWindow = window.open(viewerUrl, '_blank');

    // If preview window is blocked or fails, fallback to download
    setTimeout(() => {
      if (previewWindow.closed || !previewWindow) {
        console.log('Preview failed, downloading instead');
        const link = document.createElement('a');
        link.href = finalUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }, 1000);
  } else if (fileName.match(/\.(jpg|jpeg|png|gif|bmp|svg|webp)$/)) {
    // Images - open in new tab for viewing
    window.open(finalUrl, '_blank');
  } else {
    // Other files - let browser decide
    window.open(finalUrl, '_blank');
  }
};

// Render PDF from signed URL by fetching as blob
export const renderPdf = async (signedUrl) => {
  try {
    const response = await fetch(signedUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.status}`);
    }

    const blob = await response.blob();
    // Create blob with correct MIME type
    const pdfBlob = new Blob([blob], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    return url;
  } catch (error) {
    console.error('Error rendering PDF:', error);
    return null;
  }
};

// Format date
export const formatDate = (dateString) => {
  console.log('date recieved',dateString)
  console.log('date type', typeof dateString)
  // FIXED: Handle null/undefined dates for new uploads
  if (!dateString || typeof dateString !== 'string') {
    return 'Just now'; // Show "Just now" for new uploads
  }
  
  // Backend is storing local time with UTC suffix (bug), so we need to treat it as local time
  // Remove the "Z" and parse as local time
  const date = new Date(dateString.replace('Z', '')); // Parse as local time
  const now = new Date();

  const diffTime = Math.abs(now - date);
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Granular time formatting for recent uploads
  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }

  // Day-based formatting for older uploads
  if (diffDays === 1) {
    return 'Today';
  } else if (diffDays === 2) {
    return 'Yesterday';
  } else if (diffDays <= 7) {
    return `${diffDays - 1} days ago`;
  } else if (diffDays <= 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffDays <= 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
};
